/**
 * Resource deposits — the extraction loop's supply side.
 *
 * Node placement is a pure function of the planet seed (the terrain
 * contract): every client and the server derive identical node positions,
 * kinds and capacities with zero data transfer. What IS authoritative is
 * DEPLETION: the server owns how much is left in each node per room and
 * validates every mining claim — the first gameplay-authoritative state.
 *
 * Kind distribution follows archetype: hot worlds vein VOLATITE (fuel ore),
 * cold worlds CRYONITE, rocky worlds FERRITE — so "where do I refuel"
 * becomes a navigation question, which is the whole point.
 */
import { rngFromString, range } from "./rng.js";
import * as pmath from "./pmath.js";
import { PlanetSampler, type PlanetDef, type TerrainSample } from "./universe.js";
import { settlementInfluenceAt } from "./worldgen/settlements.js";
import { hiddenResourceNodes, type HiddenSystemManifestV1 } from "./hiddenUniverse.js";

export interface NodeKindDef {
  /** Item granted per mining cycle. */
  item: string;
  name: string;
  /** Display color 0xRRGGBB (client shard tint). */
  color: number;
}

export const NODE_KINDS: Record<string, NodeKindDef> = {
  volatite: { item: "ore-volatite", name: "Volatite Vein", color: 0xff8c3a },
  ferrite: { item: "ore-ferrite", name: "Ferrite Seam", color: 0xb8c4d4 },
  cryonite: { item: "ore-cryonite", name: "Cryonite Bloom", color: 0x7fd8ff },
};

/** Mining cycle length (seconds of held beam per unit extracted). */
export const MINE_CYCLE_S = 1.4;
/** On-foot mining reach (m from the node). */
export const MINE_RANGE_FOOT = 9;
/** Ship mining-beam reach (m from the node). */
export const MINE_RANGE_SHIP = 160;
/** Server-side claim slack (covers latency + interp drift). */
export const MINE_RANGE_SLACK = 80;
/** Empty nodes refill after this many seconds (per room). */
export const NODE_RESPAWN_S = 240;

export interface ResourceNode {
  /** Unique within the system: planetIndex * 100 + i. */
  id: number;
  /** Index into system.planets. */
  planet: number;
  /** Unit direction from the planet center (drape with the sampler). */
  dir: [number, number, number];
  kind: string;
  /** Units extractable before the node runs dry. */
  capacity: number;
}

/** Archetype → [volatite, ferrite, cryonite] weights. */
function kindWeights(def: PlanetDef): [number, number, number] {
  switch (def.archetype) {
    case "volcanic":
      return [6, 2, 0];
    case "desert":
      return [4, 3, 0];
    case "ice":
      return [0, 2, 6];
    case "barren":
      return [1, 6, 1];
    default:
      return [2, 3, 2]; // terran/ocean: a little of everything
  }
}

/**
 * The deposits on one planet — pure function of its seed. Moons run fewer,
 * richer nodes (they're the prospector's detour).
 */
export function planetNodes(def: PlanetDef, planetIndex: number): ResourceNode[] {
  const rng = rngFromString(`${def.seed}:nodes`);
  const moon = !!def.parentId;
  // Roughly doubled vs. the old worlds so deposits stay findable across the
  // much larger WORLD_SCALE'd surfaces (still sparse — a detour, not a carpet).
  const count = moon ? 6 + Math.floor(rng() * 5) : 12 + Math.floor(rng() * 9);
  const [wv, wf, wc] = kindWeights(def);
  const total = wv + wf + wc;
  const sampler = new PlanetSampler(def);
  const nodes: ResourceNode[] = [];
  const surface={} as TerrainSample;
  for (let i = 0; i < count; i++) {
    let x = rng() * 2 - 1;
    let y = rng() * 2 - 1;
    let z = rng() * 2 - 1;
    const l = pmath.hypot(x, y, z) || 1;
    let roll = rng() * total;
    let kind = "ferrite";
    if (roll < wv) kind = "volatite";
    else if (roll < wv + wf) kind = "ferrite";
    else kind = "cryonite";
    const capacity = Math.round(range(rng, moon ? 8 : 5, moon ? 16 : 11));
    // Grown settlement fabric owns its parcels: no veins in town. Draws above stay
    // fixed-order, so skipping keeps the rng stream (and ids) aligned.
    if (settlementInfluenceAt(sampler, x / l, y / l, z / l, 10) !== null) continue;
    if(def.archetype==='volcanic'&&(sampler.sample(x/l,y/l,z/l,surface,false).lavaDepthM??0)>0)continue;
    nodes.push({
      id: planetIndex * 100 + i,
      planet: planetIndex,
      dir: [x / l, y / l, z / l],
      kind,
      capacity,
    });
  }
  return nodes;
}

/** Every node in a system, keyed by id. */
export function systemNodes(planets: PlanetDef[], hidden?: HiddenSystemManifestV1 | null): Map<number, ResourceNode> {
  const map = new Map<number, ResourceNode>();
  planets.forEach((p, i) => {
    for (const n of planetNodes(p, i)) map.set(n.id, n);
  });
  for (const n of hiddenResourceNodes(hidden)) {
    if (planets[n.planet] && !map.has(n.id)) map.set(n.id, n);
  }
  return map;
}
